import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, RefreshCw, Plus, CircleDot, CircleDashed, FlaskConical } from 'lucide-react';
import uapiIntegrationsService from '../services/UapiIntegrationsService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import SearchSelect from './shared/SearchSelect';
import { formatDateTime } from '../utils/datetime';
import './UapiIntegrations.css';

// Admin > Platform > Universal API > UAPI Integrations.
//
// The code that translates a Universal API call into one backend's dialect — OHIP today, RMS Cloud next.
// It is authored here, held in the database, and run by the delegation lambda without a deploy: see
// UAPI-INTEGRATIONS.md for why, and V439 for the tables.
//
// ONE ROW PER SYSTEM, because that is the design: an integration belongs to the `marketplace` the
// x-hotelid resolves to, and a second one for the same system would make "which code runs" a question
// with two answers. The create form therefore offers only systems that do not have one yet — the server
// sends that list with the rows, so the screen never offers a choice that will be refused.
//
// WHAT THE ROW HAS TO SAY IS "IS ANYTHING LIVE", not "does a row exist". An integration with no live
// version answers 501 to every call, exactly as if it had never been created, and that is invisible
// unless the list says so.

function UapiIntegrationsPage() {
    const navigate = useNavigate();

    const [integrations, setIntegrations] = useState([]);
    const [systems, setSystems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ marketplace_id: '', name: '', description: '' });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await uapiIntegrationsService.list();
            const data = res?.data || res || {};
            setIntegrations(Array.isArray(data.integrations) ? data.integrations : []);
            setSystems(Array.isArray(data.availableSystems) ? data.availableSystems : []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'The integrations could not be loaded');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // The name defaults to the system's own name. It is editable because an integration may serve a
    // system under a name the team uses for it, but nobody should have to type "Opera Cloud via OHIP"
    // to get started.
    const pickSystem = (id) => {
        const sys = systems.find(s => String(s.marketplace_id) === String(id));
        setForm(f => ({ ...f, marketplace_id: id, name: f.name || (sys ? sys.system_name : '') }));
    };

    // The picker takes { id, label, code }; the system key is the code, which is what somebody who knows
    // a system as OHIP rather than as "Opera Cloud" will type.
    const systemItems = useMemo(() => (systems || []).map(s => ({
        id: s.marketplace_id,
        label: s.system_name,
        code: s.system_key,
    })), [systems]);

    const submit = async () => {
        if (!form.marketplace_id || !form.name.trim()) return;
        try {
            setSaving(true);
            const res = await uapiIntegrationsService.create({
                marketplace_id: Number(form.marketplace_id),
                name: form.name.trim(),
                description: form.description.trim() || null,
            });
            const created = res?.data || res;
            setCreating(false);
            setForm({ marketplace_id: '', name: '', description: '' });
            // Straight into the editor: creating an integration is never the goal, writing one is.
            if (created && created.id) navigate(`/admin/universal-api/integrations/${created.id}`);
            else load();
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'The integration could not be created');
            setCreating(false);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="uai-page">
            <div className="uai-header">
                <div>
                    <h1 className="uai-title">UAPI Integrations</h1>
                    <p className="uai-sub">
                        The code that talks to each backend system. Authored here, versioned in the database,
                        and run by the Universal API without a deploy.
                    </p>
                </div>
                <div className="uai-header-actions">
                    <Button variant="secondary" onClick={load} disabled={loading}>
                        <RefreshCw size={14} /> Refresh
                    </Button>
                    <Button
                        onClick={() => setCreating(true)}
                        disabled={loading || systems.length === 0}
                        /* Every system already has one — the button would only ever produce a 409. */
                        title={systems.length === 0 ? 'Every system already has an integration' : undefined}
                    >
                        <Plus size={14} /> New integration
                    </Button>
                </div>
            </div>

            {error && (
                <div className="uai-banner uai-banner-error">
                    <AlertCircle size={16} /> <span>{error}</span>
                </div>
            )}

            {loading ? (
                <div className="uai-loading"><Spinner /> Loading integrations…</div>
            ) : integrations.length === 0 ? (
                <div className="uai-empty">
                    <p>No integrations yet. Every system answers 501 until one is created and made live.</p>
                </div>
            ) : (
                <div className="uai-card">
                    <table className="uai-table">
                        <thead>
                            <tr>
                                <th>System</th>
                                <th>Integration</th>
                                <th>Live</th>
                                <th>Versions</th>
                                <th>Draft</th>
                                <th>Candidates</th>
                                <th>Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {integrations.map(row => (
                                <tr
                                    key={row.id}
                                    className="uai-row"
                                    onClick={() => navigate(`/admin/universal-api/integrations/${row.id}`)}
                                >
                                    <td>
                                        <div className="uai-system">{row.system_name}</div>
                                        <code className="uai-syskey">UAPI-{row.system_key}-…</code>
                                    </td>
                                    <td>
                                        <div className="uai-name">{row.name}</div>
                                        {row.description && <div className="uai-desc">{row.description}</div>}
                                    </td>
                                    <td>
                                        {/* The only column that answers "is this doing anything". */}
                                        {row.live_version ? (
                                            <span className="uai-badge uai-badge-live">
                                                <CircleDot size={12} /> v{row.live_version}
                                            </span>
                                        ) : (
                                            <span className="uai-badge uai-badge-idle">
                                                <CircleDashed size={12} /> not live
                                            </span>
                                        )}
                                    </td>
                                    <td>{row.published_count || 0}</td>
                                    <td>{row.draft_files || 0} file{Number(row.draft_files) === 1 ? '' : 's'}</td>
                                    <td>
                                        {row.candidate_count > 0 ? (
                                            <span className="uai-badge uai-badge-candidate">
                                                <FlaskConical size={12} /> {row.candidate_count}
                                            </span>
                                        ) : <span className="uai-muted">—</span>}
                                    </td>
                                    <td className="uai-muted">{formatDateTime(row.updated_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {creating && (
                <ConfirmModal
                    title="New integration"
                    confirmLabel={saving ? 'Creating…' : 'Create'}
                    onConfirm={submit}
                    onCancel={() => setCreating(false)}
                >
                    <div className="uai-form">
                        <label className="uai-label" htmlFor="uai-system">Backend system</label>
                        {/* SEARCHABLE, because the list is every system on the platform — dozens of
                            them, alphabetical, and a native <select> can only be scrolled. Same control
                            the hotel pickers use (shared/SearchSelect): type part of the name or the
                            key, arrow keys work, Enter chooses. */}
                        <SearchSelect
                            id="uai-system"
                            items={systemItems}
                            value={form.marketplace_id}
                            onChange={(next) => pickSystem(next == null ? '' : next)}
                            placeholder="Choose a system…"
                            searchPlaceholder="Search systems…"
                            emptyLabel="Every system already has an integration."
                        />
                        <p className="uai-hint">
                            Only systems without an integration are listed — there is one per system.
                        </p>

                        <label className="uai-label">Name</label>
                        <input
                            className="uai-input"
                            value={form.name}
                            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="Opera Cloud via OHIP"
                        />

                        <label className="uai-label">Description</label>
                        <input
                            className="uai-input"
                            value={form.description}
                            onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                            placeholder="Optional"
                        />
                    </div>
                </ConfirmModal>
            )}
        </div>
    );
}

export default UapiIntegrationsPage;
